/**
 * StatCard Component
 * Displays a single metric with trend indicator
 */

import { Card, CardContent } from "@/shared/components/ui";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface StatCardProps {
  title: string;
  value: string | number;
  trend?: {
    direction: "up" | "down" | "stable";
    value: string;
    label?: string;
  };
  icon?: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  isLoading?: boolean;
}

export function StatCard({
  title,
  value,
  trend,
  icon,
  iconBgColor = "bg-gray-100",
  iconColor = "text-gray-600",
  isLoading = false,
}: StatCardProps) {
  const getTrendColor = (direction: "up" | "down" | "stable") => {
    switch (direction) {
      case "up":
        return "text-success-600";
      case "down":
        return "text-error-600";
      case "stable":
        return "text-gray-500";
    }
  };

  const getTrendIcon = (direction: "up" | "down" | "stable") => {
    switch (direction) {
      case "up":
        return <TrendingUp className="w-4 h-4" />;
      case "down":
        return <TrendingDown className="w-4 h-4" />;
      case "stable":
        return <Minus className="w-4 h-4" />;
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          {icon && (
            <div className={`w-10 h-10 ${iconBgColor} rounded-lg flex items-center justify-center`}>
              <div className={iconColor}>{icon}</div>
            </div>
          )}
          <div className={`${icon ? "text-right" : ""} flex-1`}>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-2xl font-bold text-gray-900">
              {isLoading ? "-" : value}
            </p>
            {trend && !isLoading && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${getTrendColor(trend.direction)} ${icon ? "justify-end" : ""}`}>
                {getTrendIcon(trend.direction)}
                <span>{trend.value}</span>
                {trend.label && (
                  <span className="text-gray-500 ml-1">{trend.label}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
